// src/components/HomeImageSlider.tsx
//
// Full-width auto-playing image slider for the top of the home page.
// Self-contained: autoplay, prev/next, dot indicators, RTL-aware, pauses on hover.
//
// TODO: images below are placeholders (reused academy photos). Swap SLIDES for
// the real campaign/banner images when available.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Placeholder slides — 10 images.
const SLIDES = [
  '/assets/activities/champions-cup-2023.jpg',
  '/assets/activities/real-madrid-2023.jpg',
  '/assets/activities/barcelona-2022.jpg',
  '/assets/activities/basketball-camp-2023.jpg',
  '/assets/activities/karate-championship-2023.jpg',
  '/assets/activities/swimming-championship-2023.jpg',
  '/assets/activities/summer-camp-2023.jpg',
  '/assets/activities/ramadan-tournament-2023.jpg',
  '/assets/activities/sports-day-2024.jpg',
  '/assets/activities/westbank-tour-2023.jpg',
];

const AUTOPLAY_MS = 4500;

export function HomeImageSlider() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = SLIDES.length;
  const go = useCallback((i: number) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  // autoplay
  useEffect(() => {
    if (paused) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [paused, count]);

  // RTL: the "previous" visual arrow should move forward, and vice-versa.
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <section
      aria-label={isRTL ? 'معرض الصور' : 'Image slider'}
      className="relative w-full overflow-hidden bg-surface-1"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[42vh] min-h-[260px] w-full sm:h-[55vh] md:h-[65vh]">
        <AnimatePresence mode="sync">
          <motion.img
            key={index}
            src={SLIDES[index]}
            alt=""
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: 'easeInOut' }}
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
          />
        </AnimatePresence>

        {/* subtle bottom scrim for contrast against controls */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" />

        {/* arrows */}
        <button
          type="button"
          onClick={prev}
          aria-label={isRTL ? 'التالي' : 'Previous'}
          className="absolute start-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/30 p-2.5 text-white backdrop-blur transition hover:bg-black/50 md:p-3"
        >
          <PrevIcon className="h-5 w-5 md:h-6 md:w-6" />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label={isRTL ? 'السابق' : 'Next'}
          className="absolute end-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/30 p-2.5 text-white backdrop-blur transition hover:bg-black/50 md:p-3"
        >
          <NextIcon className="h-5 w-5 md:h-6 md:w-6" />
        </button>

        {/* dots */}
        <div className="absolute inset-x-0 bottom-4 z-10 flex items-center justify-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`${isRTL ? 'الشريحة' : 'Slide'} ${i + 1}`}
              aria-current={i === index}
              className={cn(
                'h-2 rounded-full transition-all duration-300',
                i === index ? 'w-6 bg-accent' : 'w-2 bg-white/60 hover:bg-white/90',
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
